/**
 * Finds 首页无限滚动：自 finds-revamp/main.js 抽取的纯函数（无 DOM 查询副作用）
 */

const SCROLL_PX_PER_SECOND = 2
const MIN_SCROLL_DURATION_MS = 180000
const MAX_SCROLL_DURATION_MS = 540000
// 小屏横向轨道仅在 setupHorizontalTrack 使用，倍率越大越慢。
const MOBILE_SCROLL_DURATION_RATIO = 50
const MOBILE_MAX_SCROLL_DURATION_MS = 2000000
const CATEGORY_SCROLL_PX_PER_SECOND = 12
const CATEGORY_MIN_DURATION_MS = 65000
const CATEGORY_MAX_DURATION_MS = 260000

export function parseGapPx(el) {
  if (!el) return 13
  const raw = getComputedStyle(el).gap || getComputedStyle(el).rowGap || '0'
  const token = String(raw).trim().split(/\s+/)[0] || '0'
  const n = parseFloat(token)
  if (Number.isNaN(n)) return 13
  if (token.includes('rem')) {
    return n * parseFloat(getComputedStyle(document.documentElement).fontSize)
  }
  return n
}

function blockHeight(elements, gapPx) {
  let total = 0
  elements.forEach((el, i) => {
    total += el.offsetHeight
    if (i < elements.length - 1) total += gapPx
  })
  return total
}

function blockWidth(elements, gapPx) {
  let total = 0
  elements.forEach((el, i) => {
    total += el.offsetWidth
    if (i < elements.length - 1) total += gapPx
  })
  return total
}

/**
 * 重建跑马灯前回收历史克隆节点，避免多次初始化导致 DOM 无限膨胀。
 * @param {HTMLElement} track
 * @param {number} periodLen
 */
function resetTrackToPeriod(track, periodLen) {
  if (!track || periodLen < 1) return
  const children = Array.from(track.children)
  children.forEach((node, idx) => {
    if (idx >= periodLen) {
      node.remove()
    }
  })
}

export function measureLoopDistance(track, originals, firstClone, first, gapPx) {
  let d = firstClone.offsetTop - first.offsetTop
  if (d > 0) return d
  d = firstClone.getBoundingClientRect().top - first.getBoundingClientRect().top
  if (d > 0) return d
  return blockHeight(originals, gapPx)
}

export function scrollDurationFromDistance(loopDistance) {
  const px = Math.max(1, Number(loopDistance) || 1)
  const duration = (px / SCROLL_PX_PER_SECOND) * 1000
  return Math.max(MIN_SCROLL_DURATION_MS, Math.min(MAX_SCROLL_DURATION_MS, duration))
}

/**
 * 小屏横向轨道时长：基于基础时长按倍率放大，确保体感明显变慢。
 * @param {number} loopDistance
 */
export function mobileScrollDurationFromDistance(loopDistance) {
  const baseDuration = scrollDurationFromDistance(loopDistance)
  const duration = baseDuration * MOBILE_SCROLL_DURATION_RATIO
  return Math.min(MOBILE_MAX_SCROLL_DURATION_MS, duration)
}

export function trackPhaseSeed(track) {
  if (!track.dataset) return Math.random()
  const existing = Number(track.dataset.marqueePhaseSeed)
  if (Number.isFinite(existing) && existing >= 0 && existing <= 1) return existing
  const next = Math.random()
  track.dataset.marqueePhaseSeed = String(next)
  return next
}

export function startMarquee(track, loopDistance, reverse) {
  if (typeof track.animate !== 'function') return

  if (track._marquee) {
    track._marquee.cancel()
    track._marquee = null
  }

  track.style.animation = 'none'

  const a = `translateY(-${loopDistance}px)`
  const keyframes = reverse
    ? [{ transform: a }, { transform: 'translateY(0)' }]
    : [{ transform: 'translateY(0)' }, { transform: a }]

  const duration = scrollDurationFromDistance(loopDistance)
  track._marquee = track.animate(keyframes, {
    duration,
    iterations: Infinity,
    easing: 'linear'
  })
  track._marquee.currentTime = duration * trackPhaseSeed(track)
}

/**
 * 纵向商品墙 track：首段为 Vue 渲染的若干 .find-card，按周期复制直至足够高
 * @param {HTMLElement} track
 * @param {number} periodLen 单套卡片数量（与 shelf 列表长度一致）
 */
export function setupSeamlessTrack(track, periodLen) {
  const parentColumn = track.closest('.column')
  if (!parentColumn || periodLen < 1) return

  resetTrackToPeriod(track, periodLen)
  const all = Array.from(track.children)
  const originals = all.slice(0, periodLen)
  if (!originals.length) return

  originals.forEach((item) => {
    track.appendChild(item.cloneNode(true))
  })

  const firstClone = track.children[originals.length]
  if (!firstClone) return

  const gapPx = parseGapPx(track)
  const minScroll = Math.max(parentColumn.clientHeight * 2, 1)
  while (track.scrollHeight < minScroll) {
    originals.forEach((item) => {
      track.appendChild(item.cloneNode(true))
    })
  }

  const first = originals[0]
  const loopDistance = measureLoopDistance(track, originals, firstClone, first, gapPx)
  if (loopDistance <= 0) return

  const reverse = parentColumn.classList.contains('reverse')
  startMarquee(track, loopDistance, reverse)
}

/**
 * 横向商品墙 track：首段为 Vue 渲染的若干 .find-card，按周期复制直至足够宽
 * @param {HTMLElement} track
 * @param {number} periodLen 单套卡片数量
 */
export function setupHorizontalTrack(track, periodLen) {
  const parentColumn = track.closest('.column')
  if (!parentColumn || periodLen < 1 || typeof track.animate !== 'function') return

  resetTrackToPeriod(track, periodLen)
  const all = Array.from(track.children)
  const originals = all.slice(0, periodLen)
  if (!originals.length) return

  originals.forEach((item) => {
    track.appendChild(item.cloneNode(true))
  })

  const firstClone = track.children[originals.length]
  const first = originals[0]
  if (!firstClone || !first) return

  const gapPx = parseGapPx(track)
  const minScroll = Math.max(parentColumn.clientWidth * 2.4, 1)
  while (track.scrollWidth < minScroll) {
    originals.forEach((item) => {
      track.appendChild(item.cloneNode(true))
    })
  }

  let loopDistance = firstClone.offsetLeft - first.offsetLeft
  if (loopDistance <= 0) {
    loopDistance = firstClone.getBoundingClientRect().left - first.getBoundingClientRect().left
  }
  if (loopDistance <= 0) {
    loopDistance = blockWidth(originals, gapPx)
  }
  if (loopDistance <= 0) return

  if (track._marquee) {
    track._marquee.cancel()
    track._marquee = null
  }

  track.style.animation = 'none'
  const duration = mobileScrollDurationFromDistance(loopDistance)
  track._marquee = track.animate(
    [{ transform: 'translateX(0)' }, { transform: `translateX(-${loopDistance}px)` }],
    {
      duration,
      iterations: Infinity,
      easing: 'linear'
    }
  )
  track._marquee.currentTime = duration * trackPhaseSeed(track)
}

export function categoryDurationFromDistance(loopDistance) {
  const px = Math.max(1, Number(loopDistance) || 1)
  const duration = (px / CATEGORY_SCROLL_PX_PER_SECOND) * 1000
  return Math.max(CATEGORY_MIN_DURATION_MS, Math.min(CATEGORY_MAX_DURATION_MS, duration))
}

export function categoryLoopDistance(row, originals, firstClone, first, gapPx) {
  let d = firstClone.offsetLeft - first.offsetLeft
  if (d > 0) return d
  d = firstClone.getBoundingClientRect().left - first.getBoundingClientRect().left
  if (d > 0) return d
  let total = 0
  originals.forEach((el, i) => {
    total += el.offsetWidth
    if (i < originals.length - 1) total += gapPx
  })
  return total
}

/**
 * 类目横条：初始 DOM 内含多套相同类目序列（由 Vue v-for 生成）
 * @param {HTMLElement} row
 * @param {number} periodLen 单套类目链接数量
 */
export function setupCategoryMarquee(row, periodLen) {
  if (!row || typeof row.animate !== 'function' || periodLen < 2) return 0

  resetTrackToPeriod(row, periodLen)
  const originals = Array.from(row.children).slice(0, periodLen)
  if (originals.length < 2) return 0

  originals.forEach((item) => {
    row.appendChild(item.cloneNode(true))
  })
  const firstClone = row.children[originals.length]
  const first = originals[0]
  if (!firstClone || !first) return 0

  const gapPx = parseGapPx(row)
  const parentWidth = Math.max(row.parentElement?.clientWidth || 0, 1)
  while (row.scrollWidth < parentWidth * 2.5) {
    originals.forEach((item) => {
      row.appendChild(item.cloneNode(true))
    })
  }

  const loopDistance = categoryLoopDistance(row, originals, firstClone, first, gapPx)
  if (loopDistance <= 0) return 0

  if (row._marquee) {
    row._marquee.cancel()
    row._marquee = null
  }

  row._marquee = row.animate(
    [{ transform: 'translateX(0)' }, { transform: `translateX(-${loopDistance}px)` }],
    {
      duration: categoryDurationFromDistance(loopDistance),
      iterations: Infinity,
      easing: 'linear'
    }
  )

  return loopDistance
}
